import "moment/locale/es"
import "src/utils/yup/yup.custom"
import { lazy, Suspense, useEffect, useState } from "react"
import "./App.css"
import { useTranslation } from "react-i18next"
import { esES, enUS } from "@material-ui/core/locale"
import MomentUtils from "@date-io/moment"
import { SnackbarProvider } from "notistack"
import { useDispatch, useSelector } from "react-redux"
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
} from "react-router-dom"
import { ThemeProvider } from "@material-ui/core"
import { MuiPickersUtilsProvider } from "@material-ui/pickers"

import GlobalStyles from "./components/Common/GlobalStyles"
import LoadingScreen from "./components/Common/LoadingScreen"
import ProtectedRoute from "./components/Common/ProtecteRoute"

import SuperAdmin from "./views/viewSuperAdministrador"
import RootEmpresa from "./views/viewEmpresa"
import RootParkey from "./views/viewParkey"
import Admin from "./views/viewAdministrador"
import Guardia from "./views/viewGuardia"
import Directiva from "./views/viewDirectiva"

import { signOut } from "./redux/slices/authSlice"
import { getCustomTheme } from "./redux/slices/themeSlice"
import { auth } from "./Firebase"
import { Roles } from "./utils/Constants"
import { customTheme } from "./theme"
import { ParkPay } from "./views/viewParkey/ParkPay"

const ViewLogin = lazy(() => import("./views/login"))
const ViewMfa = lazy(() => import("./views/login/mfa"))
const PuntoVenta = lazy(() => import("./views/viewPuntoVenta"))
const Profile = lazy(() => import("./views/profile"))
const Unauthorized = lazy(() => import("./views/errors/Unauthorized"))
const NotFoundView = lazy(() => import("./views/errors/NotFoundView"))

const Paths = [
  { path: "/administrador", roles: [Roles.admin], component: Admin },
  { path: "/superAdmin", roles: [Roles.superadmin], component: SuperAdmin },
  { path: "/empresa", roles: [Roles.company], component: RootEmpresa },
  { path: "/guardia", roles: [Roles.supervisor], component: Guardia },
  { path: "/parkey", roles: [Roles.parkey], component: RootParkey },
  { path: "/directiva", roles: [Roles.directiva], component: Directiva },
  { path: "/puntoVenta", roles: [], component: PuntoVenta },
]

const App = () => {
  const dispatch = useDispatch()
  const isAuth = useSelector(state => state.auth.isAuth)
  const companyTheme = useSelector(getCustomTheme)
  const { i18n } = useTranslation()

  const [theme, setTheme] = useState(customTheme(companyTheme))

  const getLocale = lang => {
    switch (lang) {
      case "es":
        return esES
      case "en":
        return enUS
      default:
        return enUS
    }
  }

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (!user) dispatch(signOut())
    })
    return unsubscribe
  }, [dispatch, isAuth])

  useEffect(() => {
    setTheme(customTheme(companyTheme, getLocale(i18n.language)))
  }, [companyTheme, i18n.language])

  return (
    <ThemeProvider theme={theme}>
      <MuiPickersUtilsProvider utils={MomentUtils}>
        <SnackbarProvider dense maxSnack={3}>
          <Suspense fallback={<LoadingScreen />}>
            <Router>
              <GlobalStyles />
              <Switch>
                <Route
                  path="/"
                  exact
                  render={props => <ViewLogin {...props} />}
                />
                <Route
                  path="/login/mfa"
                  render={props => <ViewMfa {...props} />}
                />
                <ProtectedRoute
                  roles={[Roles.admin, Roles.supervisor]}
                  path="/perfil"
                  exact
                  component={Profile}
                />
                <ProtectedRoute
                  roles={[Roles.admin, Roles.supervisor]}
                  path="/perfil/two_factor_auth"
                  exact
                  component={Profile}
                />
                {Paths.map(item => (
                  <ProtectedRoute
                    roles={item.roles}
                    path={item.path}
                    component={item.component}
                    key={item.path}
                  />
                ))}
                <Route
                  path="/paypark"
                  render={props => <ParkPay {...props} />}
                />
                <Route
                  path="/unauthorized"
                  render={props => <Unauthorized {...props} />}
                />
                <Route
                  path="/error"
                  render={props => <NotFoundView {...props} />}
                />
                <Redirect to="/error" />
              </Switch>
            </Router>
          </Suspense>
        </SnackbarProvider>
      </MuiPickersUtilsProvider>
    </ThemeProvider>
  )
}

export default App
